import { LitElement, html } from 'lit';
import { dispatchEvent } from '@fil-global/shared';

import '@fil-global/fil-button';
import '@fil-global/fil-select';
import '@fil-global/fil-text-input';

import componentStyles from './fil-pagination.styles.js';

/**
 * @tag fil-pagination
 * @property {number} [currentPage] Current page of the component
 * @property {object} [dropdown] Dropdown list of the component
 * @property {string} [hyphen] Separator (hyphen) in the label  of the component
 * @property {number} [items] Total items of the component
 * @property {string} [labelNext="Next"] Details of next button in the component
 * @property {string} [labelPrevious="Previous"] Details of previous button in the component
 * @property {number} [pages] Pages count of the component
 * @fires change.gds.pagination (event: Event) => void
 * @fires ready.gds.pagination (event: Event) => void
 */
export class FilPagination extends LitElement {
  static properties = {
    currentPage: { type: Number, reflect: true },
    dropdown: { type: Object },
    hyphen: { type: String },
    items: { type: Number },
    labelNext: { type: String },
    labelPrevious: { type: String },
    pages: { type: Number },
  };

  static styles = [componentStyles];

  constructor() {
    super();
    this.currentPage = 1;
    this.hyphen = 'of';
    this.items = 0;
    this.labelNext = 'Next';
    this.labelPrevious = 'Previous';

    this.itemsPerPage = 1;
  }

  getPages() {
    return Math.ceil(this.items / this.itemsPerPage);
  }

  checkPageBoundaries(currentPage) {
    return currentPage > this.pages ? this.pages : currentPage < 1 ? 1 : currentPage;
  }

  changePage(currentPage) {
    this.currentPage = this.checkPageBoundaries(currentPage);
  }

  isBackwardDisabled() {
    return this.currentPage === 1;
  }

  isForwardDisabled() {
    return this.currentPage === this.pages;
  }

  nextPage() {
    if (!this.isForwardDisabled()) {
      this.changePage(this.currentPage + 1);
    }
  }

  previousPage() {
    if (!this.isBackwardDisabled()) {
      this.changePage(this.currentPage - 1);
    }
  }

  pageChangeHandler(event) {
    // Text input is of type 'text' so we need to check if 'value' contains numbers only. If not
    // fallback to 'this.currentPage'.
    const value = +event.target.value;
    const currentPage = Number.isNaN(value) ? this.currentPage : this.checkPageBoundaries(value);

    event.target.value = currentPage;
    this.changePage(currentPage);
  }

  update() {
    // initialize default number of total pages
    this.pages = this.getPages();
    super.update();
  }

  updated(changedProperties) {
    if (changedProperties.has('dropdown')) {
      setTimeout(() => {
        if (this.dropdown && Object.keys(this.dropdown).length !== 0) {
          const select = this.dropdown.options
            ? this.shadowRoot.querySelector('fil-select').shadowRoot.querySelector('select')
            : null;
          if (select) {
            this.itemsPerPage = select.value;
            this.changePage(this.currentPage);
            dispatchEvent(this, 'ready.gds.pagination', {
              value: {
                active: this.currentPage,
                itemsPerPage: this.itemsPerPage,
              },
            });
          } else {
            this.itemsPerPage = 1;
          }
        } else {
          this.itemsPerPage = 1;
        }

        this.pages = this.getPages();
      }, 0);
    }

    if (
      changedProperties.has('currentPage') &&
      changedProperties.get('currentPage') !== undefined
    ) {
      // If 'currentPage' was changed programmatically to a value outside of boundaries, we will not
      // trigger a change event. 'currentPage' value will be fixed in 'changePage' and 'updated' will be
      // called again with a correct value, now triggering the event. If it was changed due to
      // user interaction it should always be inside boundaries, instantly triggering it.
      if (this.currentPage <= this.pages && this.currentPage >= 1) {
        dispatchEvent(this, 'change.gds.pagination', {
          value: {
            active: this.currentPage,
            itemsPerPage: this.itemsPerPage,
          },
        });
      }
      this.changePage(this.currentPage);
    }
  }

  render() {
    return html`<nav class="fil-pagination" role="navigation" aria-label="Pagination Navigation">
      ${this.dropdown && this.dropdown.options && this.dropdown.options.length
        ? html`<div class="fil-pagination__dropdown">
            <span>${this.dropdown.label}</span>
            <fil-select
              value=${JSON.stringify(this.dropdown.value)}
              options="${JSON.stringify(this.dropdown.options)}"
              @change.gds.select=${event => {
                event.stopPropagation();

                this.itemsPerPage = +Number(event.detail.value[0]);
                this.pages = this.getPages();
                // this.currentPage = 1;

                dispatchEvent(this, 'change.gds.pagination', {
                  value: {
                    active: this.currentPage,
                    itemsPerPage: this.itemsPerPage,
                  },
                });
                this.changePage(this.currentPage);
              }}
            ></fil-select>
          </div>`
        : null}
      <div class="fil-pagination__buttons">
        <fil-button
          aria-label="Goto Previous Page"
          ?disabled=${this.isBackwardDisabled()}
          icon="custom-arrow-left"
          iconPosition="left"
          kind="tertiary"
          label=${this.labelPrevious}
          size="standard"
          @click=${() => this.previousPage()}
        ></fil-button
        ><fil-button
          aria-label="Goto Previous Page"
          class="fil-pagination__buttons-mobile"
          ?disabled=${this.isBackwardDisabled()}
          icon="custom-arrow-left"
          iconPosition="left"
          kind="tertiary"
          size="standard"
          @click=${() => this.previousPage()}
        ></fil-button
        ><span class="fil-pagination__buttons-input"
          ><fil-text-input
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            min="1"
            max="${this.pages}"
            @keyup=${event => {
              if (event.keyCode === 13) {
                this.pageChangeHandler(event);
              }
            }}
            aria-current="${this.currentPage}"
            @blur.gds.input=${event => this.pageChangeHandler(event)}
            value=${this.currentPage}
          ></fil-text-input
          >${this.hyphen}&nbsp;${this.pages}</span
        ><fil-button
          aria-label="Goto Next Page"
          ?disabled=${this.isForwardDisabled()}
          icon="custom-arrow-right"
          iconPosition="right"
          kind="tertiary"
          label=${this.labelNext}
          size="standard"
          @click=${() => this.nextPage()}
        ></fil-button
        ><fil-button
          aria-label="Goto Next Page"
          class="fil-pagination__buttons-mobile"
          ?disabled=${this.isForwardDisabled()}
          iconPosition="right"
          icon="custom-arrow-right"
          kind="tertiary"
          size="standard"
          @click=${() => this.nextPage()}
        ></fil-button>
      </div>
    </nav>`;
  }
}

export default FilPagination;
